import React, { useEffect, useMemo, useRef, useState } from "react";
import { useProfile, useCurrentSpace, useUser } from "~/hooks/context-hook";
import { graphql } from "~/graphql/codegen";
import { useMutation, useQuery } from "@apollo/client/react";
import type {
  SubmitNpsSurveyResponseMutation,
  SubmitNpsSurveyResponseMutationVariables,
  GetUserNpsHistoryQuery,
  GetUserNpsHistoryQueryVariables,
} from "~/graphql/codegen/graphql";
import NPSSurvey from "./nps-survey";

/* eslint-disable lingui/no-unlocalized-strings -- Sentry tag/action labels (analytics, not user-facing) */

const SUBMIT_NPS_SURVEY_RESPONSE = graphql(`
  mutation SubmitNPSSurveyResponse(
    $score: Int!
    $feedback: String
    $mindspaceId: String
    $cohortId: String
  ) {
    submitNPSSurveyResponse(
      score: $score
      feedback: $feedback
      mindspaceID: $mindspaceId
      cohortID: $cohortId
    )
  }
`);

const GET_USER_NPS_HISTORY = graphql(`
  query GetUserNPSHistory {
    currentUser {
      nps {
        score
        comment
        timestamp
        submittedFrom
      }
    }
  }
`);
import metrics from "~/utils/metrics";
import { isFrontend } from "~/utils/is-frontend";
import { captureException } from "@sentry/react";
import { useLocation, useParams } from "react-router";
import {
  getNPSSurveyAgeDate,
  hasReachedNPSSurveyAge,
  type NPSSurveyAgeCohort,
  shouldSuppressNPSSurveyForPath,
} from "./nps-visibility";

// Constants for localStorage keys and timing
const NPS_SURVEY_DATA_KEY = "nps_survey_data";

interface NPSSurveyData {
  dismissedAt?: string;
  completedAt?: string;
}

type NPSHistoryEntry = NonNullable<
  NonNullable<GetUserNpsHistoryQuery["currentUser"]>["nps"]
>[number];

// Number of days after last submission to show survey again
const DAYS_UNTIL_RESHOW = 28;

interface NPSSurveyCohort extends NPSSurveyAgeCohort {
  id?: string | null;
  metadata?: {
    startDate?: string | null;
    endDate?: string | null;
  } | null;
}

function dateMillis(dateValue: string | null | undefined): number | null {
  if (!dateValue) return null;
  const millis = new Date(dateValue).getTime();
  return Number.isNaN(millis) ? null : millis;
}

function npsCohortRecencyMillis(cohort: NPSSurveyCohort | null | undefined): number {
  return dateMillis(getNPSSurveyAgeDate(cohort)) ?? 0;
}

export default function NPSSurveyContainer() {
  const [shouldShow, setShouldShow] = useState(false);
  // When the learner last sent or closed the card on this mount. Kept in memory so
  // their action keeps the card away even if localStorage or the network fails.
  const lastActionAtRef = useRef<number | null>(null);
  const userData = useProfile();
  const currentSpace = useCurrentSpace();
  const user = useUser();
  const referralCode = user?.referrals?.[0]?.referralCode;
  const params = useParams();
  const location = useLocation();

  const [submitNPSSurvey] = useMutation<
    SubmitNpsSurveyResponseMutation,
    SubmitNpsSurveyResponseMutationVariables
  >(SUBMIT_NPS_SURVEY_RESPONSE);
  const {
    data: npsData,
    loading: npsLoading,
    error: npsError,
  } = useQuery<GetUserNpsHistoryQuery, GetUserNpsHistoryQueryVariables>(
    GET_USER_NPS_HISTORY,
    { skip: !userData },
  );
  const frontend = isFrontend();
  const isSuppressedPath = shouldSuppressNPSSurveyForPath(location.pathname);

  const cohorts = useMemo<NPSSurveyCohort[] | undefined>(
    () =>
      currentSpace?.programs?.enrolled?.items.flatMap((item) => item.cohorts),
    [currentSpace],
  );
  let cohortID = params.cohortID;
  if (!cohortID) {
    if (cohorts) {
      const currentCohort = cohorts.find((cohort) => {
        const startDate = dateMillis(cohort?.metadata?.startDate);
        const endDate = dateMillis(cohort?.metadata?.endDate);
        const now = Date.now();
        return (
          startDate !== null &&
          endDate !== null &&
          startDate <= now &&
          endDate >= now
        );
      });
      if (currentCohort) {
        cohortID = currentCohort.id ?? undefined;
      } else {
        // Scheduled cohorts keep their start-date ordering; date-less cohorts use enrolment age.
        const latestCohort = [...cohorts].sort((a, b) => {
          return npsCohortRecencyMillis(b) - npsCohortRecencyMillis(a);
        })[0];
        cohortID = latestCohort?.id ?? undefined;
      }
    }
  }

  const currentCohortSurveyAgeDate = useMemo(() => {
    if (!cohortID) {
      return null;
    }
    const cohort = cohorts?.find((cohort) => cohort?.id === cohortID);
    return getNPSSurveyAgeDate(cohort);
  }, [cohortID, cohorts]);

  // Helper function to get NPS survey data from localStorage
  const getNPSSurveyData = (): NPSSurveyData => {
    if (!frontend) return {};

    try {
      const dataStr = localStorage.getItem(NPS_SURVEY_DATA_KEY);
      return dataStr ? JSON.parse(dataStr) : {};
    } catch (err) {
      console.error("Error parsing NPS survey data:", err);
      return {};
    }
  };

  // Helper function to save NPS survey data to localStorage
  const saveNPSSurveyData = (data: Partial<NPSSurveyData>) => {
    if (!frontend) return;

    try {
      const currentData = getNPSSurveyData();
      const updatedData = { ...currentData, ...data };
      localStorage.setItem(NPS_SURVEY_DATA_KEY, JSON.stringify(updatedData));
    } catch (err) {
      console.error("Error saving NPS survey data:", err);
    }
  };

  // Helper function to calculate days since a given date
  const getDaysSince = (date: Date): number => {
    return Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  };

  // Determine if we should show the survey based on user data
  useEffect(() => {
    if (!frontend || !userData) {
      return;
    }

    // Check if user is in a context where a program NPS prompt would be disruptive.
    if (isSuppressedPath) {
      setShouldShow(false);
      return;
    }

    if (npsLoading) {
      return;
    }

    // Unknown history: the learner may have answered on another device, so don't ask.
    if (npsError) {
      setShouldShow(false);
      return;
    }

    // Skip survey if user is not enrolled in any program
    const enrolledTotal = currentSpace?.programs?.enrolled?.total || 0;
    const isEnrolledInProgram = enrolledTotal > 0;

    if (!isEnrolledInProgram) {
      setShouldShow(false);
      return;
    }

    // Helper to check if a date is valid
    const isValidDate = (d: Date | null | undefined): d is Date =>
      d instanceof Date && !isNaN(d.getTime());

    // Get the most recent interaction date from both localStorage and DB
    let mostRecentInteractionDate: Date | null = null;

    // 1. Process localStorage data
    const localData = getNPSSurveyData();
    const completedDateLocal = localData.completedAt
      ? new Date(localData.completedAt)
      : null;
    const dismissedDateLocal = localData.dismissedAt
      ? new Date(localData.dismissedAt)
      : null;

    let latestDateLocal: Date | null = null;
    if (isValidDate(completedDateLocal) && isValidDate(dismissedDateLocal)) {
      latestDateLocal =
        completedDateLocal > dismissedDateLocal
          ? completedDateLocal
          : dismissedDateLocal;
    } else if (isValidDate(completedDateLocal)) {
      latestDateLocal = completedDateLocal;
    } else if (isValidDate(dismissedDateLocal)) {
      latestDateLocal = dismissedDateLocal;
    }

    // The learner's action on this mount counts even if localStorage did not record it
    const lastActionDate =
      lastActionAtRef.current !== null
        ? new Date(lastActionAtRef.current)
        : null;
    if (
      isValidDate(lastActionDate) &&
      (!isValidDate(latestDateLocal) || lastActionDate > latestDateLocal)
    ) {
      latestDateLocal = lastActionDate;
    }

    // 2. Process DB data (newest answer; the server appends)
    const lastNpsSubmission = npsData?.currentUser?.nps?.reduce<
      NPSHistoryEntry | undefined
    >(
      (newest, entry) =>
        !newest || entry.timestamp > newest.timestamp ? entry : newest,
      undefined,
    );
    let submissionDateDb: Date | null = null;
    if (lastNpsSubmission?.timestamp) {
      const dbDate = new Date(lastNpsSubmission.timestamp * 1000);
      if (isValidDate(dbDate)) {
        submissionDateDb = dbDate;
      }
    }

    // 3. Determine the overall most recent date
    if (isValidDate(latestDateLocal) && isValidDate(submissionDateDb)) {
      mostRecentInteractionDate =
        latestDateLocal > submissionDateDb ? latestDateLocal : submissionDateDb;
    } else if (isValidDate(latestDateLocal)) {
      mostRecentInteractionDate = latestDateLocal;
    } else if (isValidDate(submissionDateDb)) {
      mostRecentInteractionDate = submissionDateDb;
    }

    // 4. Check if we should show based on the most recent interaction date
    if (isValidDate(mostRecentInteractionDate)) {
      // A negative age (device clock behind the server) counts as recent
      const daysSinceInteraction = getDaysSince(mostRecentInteractionDate);
      if (daysSinceInteraction < DAYS_UNTIL_RESHOW) {
        setShouldShow(false);
        return; // Don't show if there was a recent interaction
      }
    } else if (mostRecentInteractionDate !== null) {
      captureException(
        new Error("NPS Survey: Invalid mostRecentInteractionDate"),
        {
          tags: { component: "NPSSurveyContainer", action: "dateCalculation" },
          extra: { localData, lastNpsSubmission },
        },
      );
      setShouldShow(false);
      return;
    }

    // Check if the user's cohort/enrolment is old enough.
    if (hasReachedNPSSurveyAge(currentCohortSurveyAgeDate)) {
      try {
        setShouldShow(true);
        metrics.track("nps_survey_shown_fe", {
          cohort_id: cohortID,
        });
      } catch (err) {
        captureException(err, {
          tags: {
            component: "NPSSurveyContainer",
            action: "processJoinedDate",
          },
          extra: {
            userId: userData?.id,
            npsSurveyAgeDate: currentCohortSurveyAgeDate,
          },
        });
      }
      return;
    }

    setShouldShow(false);
  }, [
    userData,
    currentSpace,
    frontend,
    npsData,
    npsLoading,
    npsError,
    currentCohortSurveyAgeDate,
    cohortID,
    isSuppressedPath,
  ]);

  // Handle score selection - the card keeps the score until the learner sends or closes it
  const handleSubmitScore = (score: number) => {
    if (!userData) return;

    metrics.track("nps_score_selected_fe", { score, cohort_id: cohortID });
  };

  // Shared function to submit NPS response that handles both feedback and dismissal cases
  const submitNPSResponse = async (
    score: number,
    feedback: string,
    actionType: "feedback" | "dismiss",
  ) => {
    if (!userData) return false;

    try {
      await submitNPSSurvey({
        variables: {
          score,
          feedback,
          mindspaceId: currentSpace?.id,
          cohortId: cohortID || undefined,
        },
      });

      if (actionType === "feedback") {
        metrics.track("nps_feedback_submitted_fe", {
          score,
          feedback_length: feedback.length,
          cohort_id: cohortID,
        });
      } else {
        metrics.track("nps_dismissed_after_score_fe", {
          score,
          cohort_id: cohortID,
        });
      }

      return true;
    } catch (error) {
      console.error(`Failed to submit NPS ${actionType}:`, error);
      captureException(error, {
        tags: {
          component: "NPSSurveyContainer",
          action:
            actionType === "feedback" ? "submitFeedback" : "dismissWithScore",
        },
        extra: {
          userId: userData?.id,
          score,
          spaceId: currentSpace?.id,
          ...(actionType === "feedback" && {
            feedbackLength: feedback?.length || 0,
          }),
        },
      });

      return false;
    }
  };

  // Record the learner's send or close before any network call, so a failed or
  // slow save cannot bring the card back
  const recordAction = (kind: "completed" | "dismissed") => {
    const now = new Date();
    lastActionAtRef.current = now.getTime();
    setShouldShow(false);
    saveNPSSurveyData(
      kind === "completed"
        ? { completedAt: now.toISOString(), dismissedAt: undefined }
        : { dismissedAt: now.toISOString() },
    );
  };

  // Handle feedback submission - submits both score and feedback in a single mutation
  const handleSubmitFeedback = async (score: number, feedback: string) => {
    recordAction("completed");

    // Submit in the background
    submitNPSResponse(score, feedback, "feedback").catch((error) => {
      console.error("Background NPS feedback submission failed:", error);
      captureException(error, {
        tags: {
          component: "NPSSurveyContainer",
          action: "backgroundFeedbackSubmission",
        },
        extra: {
          userId: userData?.id,
          score,
          spaceId: currentSpace?.id,
          feedbackLength: feedback?.length || 0,
        },
      });
    });
  };

  // Handle dismissal - ensures score is submitted even if feedback is dismissed
  const handleDismiss = async (score: number | null) => {
    recordAction("dismissed");

    // If a score was selected, submit it in the background even though feedback was dismissed
    if (score !== null) {
      // Don't await - let it happen in the background
      submitNPSResponse(score, "", "dismiss").catch((error) => {
        console.error("Background NPS submission failed:", error);
        captureException(error, {
          tags: {
            component: "NPSSurveyContainer",
            action: "backgroundDismiss",
          },
          extra: {
            userId: userData?.id,
            score,
            spaceId: currentSpace?.id,
          },
        });
      });
    } else {
      metrics.track("nps_dismissed_without_score_fe", {
        cohort_id: cohortID,
      });
    }
  };

  // Only render the survey component if we should show it and userData exists
  if (!userData || !shouldShow || npsLoading || npsError || isSuppressedPath) {
    return null;
  }

  return (
    <NPSSurvey
      onSubmitScore={handleSubmitScore}
      onSubmitFeedback={handleSubmitFeedback}
      onDismiss={handleDismiss}
      referralCode={referralCode}
    />
  );
}
/* eslint-enable lingui/no-unlocalized-strings */
